﻿import { useState, useEffect, useCallback } from 'react';
import { useRef } from 'react';
import { playerRepository }                  from '../../data/playerRepository';
import type { ConfiguracoesTela, UpdateSettingsBroadcast } from '@/shared/models/configuracoes.model';

export type PlayerConfig = Omit<ConfiguracoesTela, 'id' | 'tela_id' | 'empresa_id' | 'criado_em' | 'atualizado_em'>;

/**
 * Carrega as configurações da tela na inicialização.
 *
 * Retorna [config, handleUpdateSettings, recarregar].
 * - `config`               — valores atuais (defaults até a carga terminar)
 * - `handleUpdateSettings` — callback para o listener de broadcast existente
 *   no Player (canal_tela_${telaId}) evitando canal duplicado.
 * - `recarregar`           — refaz o fetch; o Player chama após reconexão,
 *   porque `update_settings` é broadcast efêmero (sem replay do que se perdeu).
 */
export function usePlayerConfig(
  telaId: string | undefined,
): [PlayerConfig, (payload: unknown) => void, () => void] {
  const [config, setConfig] = useState<PlayerConfig>(playerRepository.configDefaults());
  const telaIdAtualRef = useRef(telaId);
  const versaoCargaRef = useRef(0);
  telaIdAtualRef.current = telaId;

  const recarregar = useCallback(() => {
    if (!telaId) return;
    const versao = ++versaoCargaRef.current;
    playerRepository.buscarConfiguracoes(telaId).then(cfg => {
      if (!cfg || telaIdAtualRef.current !== telaId || versaoCargaRef.current !== versao) return;
      const { id: _id, tela_id: _tid, empresa_id: _eid, criado_em: _c, atualizado_em: _a, ...rest } = cfg;
      setConfig({ ...playerRepository.configDefaults(), ...rest });
    }).catch(() => {});
  }, [telaId]);

  useEffect(() => {
    recarregar();
    return () => { versaoCargaRef.current += 1; };
  }, [recarregar]);

  const handleUpdateSettings = useCallback((payload: unknown) => {
    const p = payload as UpdateSettingsBroadcast;
    if (p?.cmd !== 'update_settings') return;
    const { tela_id: _tid, empresa_id: _eid, ...rest } = p.configuracoes;
    setConfig(prev => ({ ...prev, ...rest }));
  }, []);

  return [config, handleUpdateSettings, recarregar];
}
